import { Injectable, computed, signal, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Leaderboard, UserRank } from '@shared/types/leaderboard';
import { overridePrizeLabel } from '@shared/utils/prize-label.utils';
import { Observable, tap, catchError, throwError, map, take } from 'rxjs';
import { FormControl } from '@angular/forms';
import { CurrencyService } from '@services/currency.service';
import { GroupedPrizePackage, PrizeData } from '@shared/types/prize-package';
import { groupConsecutivePrizes } from '@shared/utils/group-prizes.utils';

@Injectable({
  providedIn: 'root',
})
export class LeaderboardService {
  private http = inject(HttpClient);
  private currencyService = inject(CurrencyService);

  isLoading = signal<boolean>(false);
  users = signal<UserRank[]>([]);
  selectedLeaderboard = signal<Leaderboard | null>(null);

  selectedPrizes = computed(() => this.mapPrizes(this.selectedLeaderboard()));
  you = computed(() => this.users().find((user) => user.you));
  totalNumberOfUsers = computed(() => this.users().length);
  searchControl = new FormControl('');
  filteredUsers = computed(() => {
    const filteredUsers = this.getFilteredUser(
      this.searchText() ?? '',
      this.users(),
    );
    return filteredUsers.map((user, index) => {
      // find users for whom we want to add a border in the list
      if (index !== 0 && !user.you && !filteredUsers[index - 1]!.you) {
        return {
          ...user,
          showBorder: true,
        };
      }
      return user;
    });
  });

  private _cache = new Map<string, UserRank[]>();
  private searchText = toSignal(this.searchControl.valueChanges, {
    initialValue: this.searchControl.value,
  });

  constructor() {
    effect(() => {
      const lb = this.selectedLeaderboard();
      if (!lb) return;

      if (this._cache.has(lb.uuid)) {
        const cached = this._cache.get(lb.uuid)!;
        this.users.set(cached);
        return;
      }
      this.getLeaderboardRanks(lb.uuid).pipe(take(1)).subscribe();
    });
  }

  setSelectedLeaderboard(lb: Leaderboard | null): void {
    this.selectedLeaderboard.set(lb);
  }

  private mapPrizes(lb: Leaderboard | null): PrizeData[] {
    const grouped = groupConsecutivePrizes(lb?.prizePackage?.prizes);
    return grouped.map((group: GroupedPrizePackage) => {
      if (Array.isArray(group)) {
        return {
          score: `#${group[0].score}-${group[group.length - 1].score}`,
          label: group[0].prize.label || '',
        };
      }
      return {
        score: `#${group.score}`,
        label: group.prize.label || '',
      };
    });
  }

  getLeaderboardRanks(uuid: string): Observable<UserRank[]> {
    this.isLoading.set(true);
    return this.http
      .get<
        UserRank[]
      >(`${environment.backendUrl}/api/quiz/game/leaderboard/${uuid}`)
      .pipe(
        map((response) =>
          response.map((entry) => ({
            ...entry,
            prize: entry.prize
              ? {
                  ...entry.prize,
                  label: overridePrizeLabel(entry.prize, this.currencyService),
                }
              : null,
          })),
        ),
        tap((response) => {
          this._cache.set(uuid, response);
          this.users.set(response);
          this.isLoading.set(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.isLoading.set(false);
          return throwError(() => new Error(err.message));
        }),
      );
  }

  private getFilteredUser(searchText: string, users: UserRank[]): UserRank[] {
    if (!searchText) {
      return users;
    }
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(searchText.toLowerCase()) ||
        user.you,
    );
  }
}
